/**
 * Chat session handle store.
 *
 * Holds ONLY the opaque server-side conversation id — never message content.
 * Backed by an in-memory value plus `sessionStorage` so the handle:
 *  - survives a page reload (F5) → the conversation resumes, and
 *  - is wiped when the browser tab is closed → the next open starts fresh
 *    (the abandoned server-side conversation is reaped by the inactivity TTL).
 *
 * Shared by FloatingAssistant and useAuth so ending a conversation on logout
 * doesn't depend on the component being mounted.
 */
const STORAGE_KEY = 'nuguard:chat:conversation_id';

let inMemoryId: string | null = null;

function safeSessionStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    // sessionStorage can throw in some privacy modes / sandboxed iframes.
    return null;
  }
}

export function getActiveConversationId(): string | null {
  if (inMemoryId) return inMemoryId;
  const store = safeSessionStorage();
  if (store) {
    const stored = store.getItem(STORAGE_KEY);
    if (stored) {
      inMemoryId = stored;
      return stored;
    }
  }
  return null;
}

export function setActiveConversationId(conversationId: string | null): void {
  inMemoryId = conversationId;
  const store = safeSessionStorage();
  if (!store) return;
  try {
    if (conversationId) {
      store.setItem(STORAGE_KEY, conversationId);
    } else {
      store.removeItem(STORAGE_KEY);
    }
  } catch {
    /* ignore storage write failures */
  }
}

export function clearActiveConversationId(): void {
  setActiveConversationId(null);
}
